import type { ReactNode } from 'react';
import type { PermissionKey } from '@nexa/auth';
import { useAccess } from './use-access';

// <Can permission="cases.create">…</Can> — render children only when the operator holds
// the permission (or, with `anyOf`, any of them / with `allOf`, all of them). Renders
// `fallback` (default: nothing) otherwise — including while access is still loading, so a
// gated control never flashes in and out.

type CanProps = {
  children: ReactNode;
  /** Rendered when denied (and while access loads). Default: nothing. */
  fallback?: ReactNode;
} & (
  | { permission: PermissionKey; anyOf?: never; allOf?: never }
  | { anyOf: readonly PermissionKey[]; permission?: never; allOf?: never }
  | { allOf: readonly PermissionKey[]; permission?: never; anyOf?: never }
);

export function Can(props: CanProps) {
  const access = useAccess();
  const allowed =
    props.permission !== undefined
      ? access.can(props.permission)
      : props.anyOf !== undefined
        ? access.canAny(...props.anyOf)
        : access.canAll(...(props.allOf ?? []));
  return <>{allowed ? props.children : (props.fallback ?? null)}</>;
}
